
















































































































































































import { computed, defineComponent, onUnmounted, PropType, ref, toRef, watch } from "@vue/composition-api";
import { LiveChatInvitation, LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString } from "@/lib/moneyHelpers";
import { differenceInMilliseconds, getNow, parseISO, toTimePassed } from "@/lib/time";
import config from "@/config";
import { ConsumeMediaConstraints, MediaConstraints } from "@/types";
import BaseAvatar from "@/components/BaseAvatar";
import BaseButton from "@/components/BaseButton";
import BaseIcon from "@/components/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import { StreamUser } from "../../types";
import ControlButtons from "../ControlButtons";
import LiveEndConfirm from "../LiveEndConfirm";
import PrivateInvitationsList from "../PrivateInvitationsList";
import PrivateLiveEndConfirm from "../PrivateLiveEndConfirm";
import StreamChat from "../StreamChat";
import StreamChatSendMessage from "../StreamChatSendMessage";
import StreamSnapshotController from "../StreamSnapshotController";
import StreamViewOverlay from "../StreamViewOverlay";
import UserStreamsView from "../UserStreamsView";
import ViewersList from "../ViewersList";

export default defineComponent({
  name: "StreamView",
  components: {
    BaseIconButton,
    UserStreamsView,
    StreamViewOverlay,
    StreamSnapshotController,
    LiveEndConfirm,
    PrivateLiveEndConfirm,
    StreamChatSendMessage,
    ControlButtons,
    PrivateInvitationsList,
    ViewersList,
    BaseAvatar,
    BaseButton,
    BaseIcon,
    StreamChat,
  },
  props: {
    ownerId: { type: String, required: true },
    loading: { type: Boolean, required: true },
    isOwner: { type: Boolean, required: true },
    isProducing: { type: Boolean, required: true },
    token: { type: String },
    privateToken: { type: String },
    usersToConsume: { type: Array as PropType<StreamUser[]>, required: true },
    privateLiveInvitations: { type: Array as PropType<LiveChatInvitation[]>, default: () => [] },
    privateLivePrice: { type: Number },
    isPrivateLive: { type: Boolean, default: false },
    showLeavingPrivate: { type: Boolean, default: false },
    showGoingPrivate: { type: Boolean, default: false },
    ownerIsBusy: { type: Boolean, default: false },
    privateConsumer: { type: Object as PropType<UserPublicData | null> },
    ownerUser: { type: Object as PropType<UserPublicData | null> },
    localStream: { type: MediaStream, required: true },
    userList: { type: Array as () => LiveChatUser[], required: true },
    threadId: { type: String },
    privateThreadId: { type: String },
    streamStartedDate: { type: String, required: true },
    connecting: { type: Boolean, required: true },
    connectingState: { type: String, required: true },
    constraints: { type: Object as PropType<MediaConstraints> },
    consumeConstrains: { type: Object as PropType<ConsumeMediaConstraints> },
    hasManualPreview: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { authUser, authorities, isAdmin } = useAuthUser();

    const showTime = ref(true);
    const showEndingConfirm = ref(false);
    const showViewers = ref(false);
    const timePassed = ref("");
    const spent = ref("");
    const isStreamAlive = ref(false);
    const collapseUI = ref(false);

    const privateLivesAvailable = computed(
      () =>
        config.featurePrivateLives &&
        isStreamAlive.value &&
        !props.isPrivateLive &&
        !isAdmin.value &&
        (checkAuthorities(authorities.value, ["createMyLiveChatInvitation"]) || !authUser.value)
    );
    const privateButtonDisabled = computed(
      () => props.ownerIsBusy || props.privateLiveInvitations.some(item => item.senderId === authUser.value?.id)
    );
    const showUI = computed(
      () => !props.connecting && !props.loading && isStreamAlive.value && !props.showGoingPrivate
    );
    const endButtonText = computed(() =>
      props.isPrivateLive && !isAdmin.value ? $t("End show") : props.isOwner ? $t("End") : $t("Exit")
    );

    const onClose = () => {
      emit("close");
      showEndingConfirm.value = false;
    };

    const endClick = () => {
      if (isAdmin.value && props.isPrivateLive) {
        emit("returnToPublicLive");
      } else if (props.isOwner || props.isPrivateLive) {
        showEndingConfirm.value = true;
      } else {
        onClose();
      }
    };

    const handleShowViewers = () => {
      if (!props.isOwner && !isAdmin.value) return;
      showViewers.value = !showViewers.value;
    };

    let interval: ReturnType<typeof setInterval>;
    watch(toRef(props, "streamStartedDate"), streamStartedDate => {
      interval && clearInterval(interval);
      if (!streamStartedDate) {
        timePassed.value = "";
        isStreamAlive.value = false;
        return;
      }
      isStreamAlive.value = true;
      const countDate = () => {
        const passedMs = Math.max(0, differenceInMilliseconds(getNow(), parseISO(props.streamStartedDate)));
        timePassed.value = toTimePassed(passedMs);
        if (props.isPrivateLive && !props.isOwner && props.privateLivePrice) {
          spent.value = formatMoneyString((Math.floor(passedMs / 1000 / 60) + 1) * (props.privateLivePrice || 0));
        } else if (spent.value) {
          spent.value = "";
        }
      };
      countDate();
      interval = setInterval(countDate, 1000);
    });

    onUnmounted(() => interval && clearInterval(interval));

    return {
      isAdmin,
      showTime,
      showEndingConfirm,
      showViewers,
      timePassed,
      isStreamAlive,
      endClick,
      onClose,
      handleShowViewers,
      showUI,
      privateLivesAvailable,
      privateButtonDisabled,
      spent,
      collapseUI,
      endButtonText,
    };
  },
});
